import axios, { AxiosProgressEvent } from 'axios';

export type FolderNode = {
  type: 'FOLDER',
  name: string,
  _id: string,
  parentId: string,
  createdAt: Date,
  createdBy: string,
}

export type FileNode = {
  type: 'FILE',
  name: string,
  _id: string,
  parentId: string,
  createdAt: Date,
  createdBy: string,
  mimeType: string,
  size: number,
}

export type FileOrFolder = FileNode | FolderNode;

export type Result = {
  name: string,
  _id: string,
  children: FileOrFolder[],
}

type FileAreaContext = { quotationId?: string };
const ctxParams = (ctx?: FileAreaContext) =>
  ctx?.quotationId ? { quotationId: ctx.quotationId } : undefined;

class ServiceFiles {
  async uploadFile(root: string, path: string, file: File, onUploadProgress: (progressEvent: AxiosProgressEvent) => void, options?: { ttl?: boolean } & FileAreaContext): Promise<FileNode> {
    const formData = new FormData();
    formData.set('json', JSON.stringify({ name: file.name, mimeType: file.type, expectedSize: file.size, lastModified: new Date(file.lastModified), ttl: options?.ttl }));
    formData.set('file', file);
    const response = await axios.post(`/api${root}${path}`, formData, { onUploadProgress, params: ctxParams(options) });
    return response.data;
  }

  async listFiles(root: string, path: string, options?: FileAreaContext) : Promise<Result> {
    const response = await axios.get(`/api${root}${path}`, { params: ctxParams(options) });
    return response.data;
  }

  async createFolder(root: string, path: string, name: string, options?: { ttl?: boolean } & FileAreaContext) {
    const response = await axios.post(`/api${root}${path}`, { name, ttl: options?.ttl }, { params: ctxParams(options) });
    return response.data;
  }

  async deleteFileOrFolder(root: string, path: string, options?: FileAreaContext) {
    const response = await axios.delete(`/api${root}${path}`, { params: ctxParams(options) });
    return response.data;
  }

  async renameFileOrFolder(root: string, path: string, oldName: string, newName: string, options?: FileAreaContext) {
    const response = await axios.post(`/api${root}${path}`, { operation: 'RENAME', oldName, newName }, { params: ctxParams(options) });
    return response.data;
  }

  async copyFileArea(sourceRoot: string, destinationRoot: string, options?: { ttl?: boolean; replaceDestination?: boolean } & FileAreaContext): Promise<void> {
    await axios.post('/api/file-operations/copy', {
      sourceRoot,
      destinationRoot,
      ttl: options?.ttl,
      replaceDestination: options?.replaceDestination,
    }, { params: ctxParams(options) });
  }

  async downloadZip(ids: string[]): Promise<Blob> {
    const response = await axios.post('/api/file-nodes/download-zip', { ids }, { responseType: 'blob' });
    return response.data;
  }
}

export default new ServiceFiles();
